import React, { useEffect, useRef, useState } from "react";
import { PlayIcon } from "lucide-react";

import { getChatMediaBubbleBounds } from "@fitness-app/client-shared/features/chat/chat-media-bubble";
import { getGifDisplaySize } from "@fitness-app/client-shared/features/chat/gif-display-size";
import {
  canPauseGifPlayback,
  getGifPlaybackLabel,
  getGifPlaybackSource,
} from "@fitness-app/client-shared/features/chat/gif-playback";
import {
  type FileMessageContent,
  type GifAttachment as GifAttachmentMeta,
} from "@fitness-app/data-models/entities/Chat";
import { cn } from "@fitness-app/utils/src/styles/cn";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

interface GifAttachmentProps {
  file: FileMessageContent & { gif: GifAttachmentMeta };
  position?: "left" | "right";
}

/**
 * A provider GIF (TRE-2104) rendered as its own bubble instead of a 56 px file tile.
 *
 * Playback follows Facebook (TRE-2117): the bubble sits on its still frame under a play badge,
 * clicking starts the loop, and clicking again stops it and brings the badge back. Several
 * bubbles animating on their own in a long conversation is exhausting to read past.
 */
export const GifAttachment = ({ file, position }: GifAttachmentProps): React.ReactElement => {
  const { t: tUi } = useInterfaceTranslation();
  const { width, height } = getGifDisplaySize(file.gif, getChatMediaBubbleBounds());
  const source = getGifPlaybackSource(file);
  const canPause = canPauseGifPlayback(source);
  // A bubble we cannot stop has to keep looping — there is no still frame to park it on.
  const [playing, setPlaying] = useState(!canPause);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Drive the element from state rather than calling play()/pause() in the click handler: the
  // element is remounted whenever the message list re-renders around it, and an imperative call
  // made before that would be lost.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (playing) {
      // A rejected play() (a browser policy, a backgrounded tab) leaves the poster up, which is
      // the stopped look — so reflect that instead of claiming the GIF is running.
      void video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  }, [playing, source.kind]);

  const renderMedia = () => {
    if (source.kind === "video") {
      return (
        <video
          ref={videoRef}
          src={source.videoUrl}
          poster={source.posterUrl ?? undefined}
          width={width}
          height={height}
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={file.shortName}
          className="block h-full w-full object-cover"
        />
      );
    }

    // No mp4 to pause on a frame, so pausing swaps in the still preview instead.
    return (
      <img
        src={playing || !source.stillUrl ? source.animatedUrl : source.stillUrl}
        alt={file.shortName}
        title={file.shortName}
        width={width}
        height={height}
        loading="lazy"
        className="block h-full w-full object-cover"
      />
    );
  };

  const media = (
    <div
      className="relative overflow-hidden rounded-xl bg-gray-100 bg-cover bg-center"
      style={{
        width,
        height,
        backgroundImage: file.gif.previewUrl ? `url(${file.gif.previewUrl})` : undefined,
      }}
    >
      {renderMedia()}
      <span className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-black/55 px-1 text-[10px] font-semibold leading-4 text-white">
        GIF
      </span>
      {canPause && !playing ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/55 backdrop-blur-[2px]">
            <PlayIcon size={20} className="translate-x-[1px] text-white" fill="currentColor" />
          </span>
        </span>
      ) : null}
    </div>
  );

  return (
    <div className={cn("flex px-3 py-1", position === "right" ? "justify-end" : "justify-start")}>
      {canPause ? (
        <button
          type="button"
          onClick={() => setPlaying((current) => !current)}
          aria-label={getGifPlaybackLabel(tUi, { playing, name: file.shortName })}
          aria-pressed={!playing}
          className="max-w-full cursor-pointer rounded-xl border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          {media}
        </button>
      ) : (
        media
      )}
    </div>
  );
};
